/**
 * productLabelEscPos.ts — Genera bytes ESC/POS para etiquetas de productos.
 * Útil para impresión térmica de lote, vencimiento, precios y códigos de barras.
 */

import { Producto } from "@/types";
import {
  sanitizeForThermal,
  getTicketMaxWidth,
} from "@/features/caja/services/ticketRender.service";
import { loadBusinessForReceipt } from "@/features/caja/services/ticketRender.service";
import { buildCode128EscPosBytes } from "@/lib/escPosBarcode";
import { formatDate } from "@/lib/utils/formatters";

const ESC = 0x1b;
const GS = 0x1d;

export interface ProductLabelOptions {
  includeBusiness: boolean;
  includeName: boolean;
  includePrice: boolean;
  includeLote: boolean;
  includeExpiry: boolean;
  includeArrival: boolean;
  includeWeight: boolean;
  includeBarcode: boolean;
  barcodeType: "CODE128" | "QR";
  // Overrides para edición libre en preview
  businessNameOverride?: string;
  productNameOverride?: string;
  priceOverride?: string;
  loteOverride?: string;
  expiryDateOverride?: string;
  arrivalDateOverride?: string;
  weightOverride?: string;
  barcodeOverride?: string;
}

/**
 * Construye un buffer ESC/POS para una etiqueta de producto.
 */
export async function buildProductLabelEscPos(
  product: Producto,
  options: ProductLabelOptions,
  paperWidth: 58 | 80 = 58,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const maxWidth = getTicketMaxWidth(paperWidth);

  const add = (text: string) => {
    chunks.push(encoder.encode(sanitizeForThermal(text) + "\n"));
  };

  const addRaw = (bytes: number[]) => {
    chunks.push(new Uint8Array(bytes));
  };

  // 1. Inicializar
  addRaw([ESC, 0x40]);

  // 2. Centrado
  addRaw([ESC, 0x61, 0x01]);

  // 3. Info de Negocio
  if (options.includeBusiness) {
    const biz = loadBusinessForReceipt();
    const name = options.businessNameOverride || biz.nombre;

    addRaw([ESC, 0x21, 0x01]); // Fuente reducida
    add(name.toUpperCase());
    addRaw([0x0a]);
  }

  // 4. Nombre (Negrita, Doble Ancho/Alto)
  if (options.includeName) {
    const name = options.productNameOverride || product.nombre;
    addRaw([ESC, 0x21, 0x30]); // Doble ancho + alto + negrita
    add(name.substring(0, maxWidth / 2).toUpperCase());
    addRaw([ESC, 0x21, 0x00]); // Reset fuente
  }

  // 4. Marca / Categoría (si no hay override de nombre o si el usuario quiere mantenerlo)
  if (!options.productNameOverride && (product.marca || product.categoria)) {
    add(
      `${product.categoria}${product.marca ? ` - ${product.marca}` : ""}`.toUpperCase(),
    );
  }

  // 5. Precio (Destacado)
  if (options.includePrice) {
    const priceStr =
      options.priceOverride || (product.precio ?? 0).toLocaleString("es-CO");
    addRaw([ESC, 0x21, 0x08]); // Negrita
    add(`PRECIO: $${priceStr}`);
    addRaw([ESC, 0x21, 0x00]);
  }

  addRaw([ESC, 0x61, 0x00]); // Alineación izquierda

  // 6. Lote, Vencimiento, Llegada
  if (options.includeLote) {
    const lote = options.loteOverride || product.numeroLote;
    if (lote) add(`LOTE: ${lote}`.toUpperCase());
  }

  if (options.includeWeight) {
    const weight = options.weightOverride || product.pesoNeto;
    if (weight) add(`PESO: ${weight}`.toUpperCase());
  }

  if (options.includeArrival) {
    const arrival = options.arrivalDateOverride || product.fechaLlegada;
    if (arrival) add(`LLEGA: ${arrival}`.toUpperCase());
  }

  if (options.includeExpiry) {
    const expiryStr =
      options.expiryDateOverride ||
      (product.fechaVencimiento
        ? formatDate(product.fechaVencimiento, "dd/MM/yyyy")
        : "");
    if (expiryStr) {
      add(`VENCE: ${expiryStr}`.toUpperCase());
    }
  }

  // 7. Código de Barras / QR
  if (options.includeBarcode) {
    const code = options.barcodeOverride || product.codigoBarras || product.id;
    if (code) {
      addRaw([ESC, 0x61, 0x01]); // Centro

      if (options.barcodeType === "CODE128") {
        // CODE128: GS k 73 n {B data — subconjunto B para máxima compatibilidad
        addRaw([GS, 0x77, 0x02]); // Ancho barra 2
        addRaw([GS, 0x68, 0x40]); // Alto barra 64
        addRaw([GS, 0x48, 0x02]); // Texto abajo
        chunks.push(buildCode128EscPosBytes(code));
      }
    }
  }

  // 8. Espacio final y corte automático (GS V 65 n — estándar; 42 no corta)
  addRaw([0x0a, 0x0a, 0x0a]); // 3 saltos
  addRaw([GS, 0x56, 65, 0x10]); // Corte con feed

  // Unir todos los Uint8Array
  const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result;
}
